import React, { useState, useEffect } from 'react';
import { Menu, X, Search } from 'lucide-react';
import { Sidebar } from '../sidebar/Sidebar';
import { SearchModal } from '../search/SearchModal';
import logoImage from '../../assets/aku_logo.svg';

interface DocLayoutProps {
  children: React.ReactNode;
}

export const DocLayout: React.FC<DocLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 fixed w-full top-0 z-30 shadow-sm">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14 gap-4">
            {/* Logo */}
            <div className="flex items-center gap-3 shrink-0">
              <img
                src={logoImage}
                alt="Aku Logo"
                className="w-[30px] sm:w-[34px] object-contain"
              />
              <div className="h-4 w-px bg-gray-200" />
              <span className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">Docs</span>
            </div>

            {/* Search bar */}
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden sm:flex flex-1 max-w-sm items-center gap-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors text-left"
            >
              <Search className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-xs">Search APIs...</span>
              <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                <kbd className="bg-white border border-gray-200 px-1 py-0.5 rounded font-mono shadow-sm">⌘</kbd>
                <kbd className="bg-white border border-gray-200 px-1 py-0.5 rounded font-mono shadow-sm">K</kbd>
              </span>
            </button>

            <div className="flex-1 sm:hidden" />

            {/* Mobile search icon + hamburger */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchOpen(true)}
                className="sm:hidden p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <Search className="h-4 w-4" />
              </button>
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none lg:hidden transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div
          className={`fixed inset-0 bg-black/60 transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={`fixed top-0 left-0 bottom-0 flex flex-col w-full max-w-xs sm:w-80 bg-[#1a0831] shadow-2xl transform transition-transform ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between h-14 px-6 border-b border-white/10">
            <img src={logoImage} alt="Aku Logo" className="w-[30px] object-contain" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-white/50 hover:text-white p-1 rounded-md transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <Sidebar />
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-80">
        <div className="hidden lg:flex lg:flex-col lg:w-80 lg:bg-[#1a0831] lg:border-r lg:border-white/10 lg:pt-14 shadow-xl">
          <div className="flex-1 overflow-y-auto">
            <Sidebar />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-80 pt-14">
        <main className="flex-1 p-5 sm:p-8 lg:p-10 min-h-screen">
          {children}
        </main>
      </div>

      {/* Search modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};
